import { supabase } from './supabase';

/**
 * Creates a Stripe checkout session for the specified price ID
 * 
 * @param priceId The Stripe price ID to create a checkout session for
 * @param mode The checkout mode ('payment' or 'subscription')
 * @returns A Promise that resolves when the checkout session is created
 */
export async function createCheckoutSession(priceId: string, mode: 'payment' | 'subscription' = 'subscription') {
  try {
    console.log('Creating checkout session for price ID:', priceId, 'mode:', mode);
    
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`;
    
    // Get the current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.error('No session found - user must be logged in');
      throw new Error('You must be logged in to create a checkout session');
    }

    console.log('User authenticated, proceeding with checkout request');
    
    // Check if a customer ID already exists for this user
    const { data: existingCustomer, error: customerError } = await supabase
      .from('stripe_customers')
      .select('customer_id')
      .eq('user_id', session.user.id)
      .maybeSingle();

    if (customerError && customerError.code !== 'PGRST116') {
      console.error('Error checking existing customer:', customerError);
    }

    const customerId = existingCustomer?.customer_id;
    console.log('Existing customer ID:', customerId || 'None found');

    // Make the request to the edge function with explicit headers
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        price_id: priceId,
        success_url: `${window.location.origin}/profile?success=true`,
        cancel_url: `${window.location.origin}/profile?canceled=true`,
        mode,
        customer_id: customerId, // Pass existing customer ID if available
        create_customer_if_missing: true
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Checkout session creation failed:', response.status, errorText);
      
      try {
        // Try to parse error as JSON if possible
        const errorData = JSON.parse(errorText);
        
        // Check specifically for customer not found error
        if (errorData.error && errorData.error.includes('No such customer')) {
          console.log('Customer not found, attempting to recreate...');
          
          // Try again with a request to recreate the customer
          const retryResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              price_id: priceId,
              success_url: `${window.location.origin}/profile?success=true`,
              cancel_url: `${window.location.origin}/profile?canceled=true`,
              mode,
              recreate_customer: true  // Signal to recreate the customer
            })
          });
          
          if (!retryResponse.ok) {
            const retryErrorText = await retryResponse.text();
            console.error('Retry checkout failed:', retryResponse.status, retryErrorText);
            throw new Error('결제 세션 생성에 실패했습니다. 다시 시도해주세요.');
          }
          
          const { url } = await retryResponse.json();
          if (url) {
            console.log('Redirecting to Stripe checkout URL after customer recreation:', url);
            window.location.href = url;
            return;
          } else {
            throw new Error('결제 URL을 찾을 수 없습니다');
          }
        }
      } catch (parseError) {
        // If error parsing fails, just use the raw error text
        console.error('Error parsing response:', parseError);
      }
      
      throw new Error('결제 세션 생성에 실패했습니다. 다시 시도해주세요.');
    }

    const responseData = await response.json();
    console.log('Checkout session created successfully:', responseData);
    
    // Redirect to Stripe Checkout
    if (responseData.url) {
      console.log('Redirecting to Stripe checkout URL:', responseData.url);
      window.location.href = responseData.url;
    } else {
      throw new Error('결제 URL을 찾을 수 없습니다');
    }
  } catch (error: any) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}